import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { CanLeaveResult, CsPage, NavController } from 'providers/nav-controller';
import { BodyCompositionDataChangedEvent, UserDb } from 'services/user-db.service';
import { DateTime } from 'luxon';
import { AuthService } from 'services/auth.service';
import { VldGroupDirective } from 'directives/vld-group/vld-group';
import { DatabaseWrapper, DayUpdatedEvent, DocWatcher } from 'services/database-wrapper';
import { DebugService } from 'services/debug.service';
import { Gender } from '@cf-shared/enums';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { TelemetryService } from 'services/telemetry.service';
import { CouchDocDraft, deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { getLastMeasuredWaist_cm, hasAnyCaliperReadings, hasAnyTapeReadings, hasMeasuredBodyFat, hasMeasuredSkinfolds, hasMeasuredWaist, Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { BF_FREQUENCY_NEW_USER_DAYS, getRecommendedBfFrequencyDays } from '@cf-shared/body-comp-cadence';
import { LogRecordType, LogRecordState, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { ImmutableState, FitnessState } from '@cf-shared/data-model/user-state';
import { PubSubService } from 'services/pubsub.service';
import { EditWeighInPageParams } from './edit-weigh-in-interface';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LoadingController } from 'providers/loading-controller';
import { NotificationService } from 'services/notification.service';
import { LoggingService } from 'services/logging.service';
import { AnimationController } from 'providers/animation-controller';
import { formatRelativeDay, skinfoldSiteName } from 'util/formatting';
import { newEntryTimeForDay } from 'utils/new-entry-time-for-day';
import { decideBodyFatSections } from './edit-weigh-in-bf-sections';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { isPlanCheckFailed } from 'services/server/plan-check-failure';
import { describeError } from '@cf-shared/error-describe';

enum DateStatus {
  Past = 'Past',
  Today = 'Today',
  Future = 'Future'
}

@Component({
  selector: 'page-edit-weigh-in',
  templateUrl: 'edit-weigh-in.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditWeighInPage extends CsPage<EditWeighInPageParams> {
  protected LL = LL;
  @ViewChild(VldGroupDirective) vldGroup: VldGroupDirective;
  // these are used by vld-* components
  progressStep = 0;
  totalSteps: number;
  checkValues = false;


  protected title = '';
  protected weighIn: Log_WeighIn; // Working copy for UI modifications
  protected weighInInitial: Log_WeighIn; // Snapshot for change detection
  private weighInDraft: CouchDocDraft<Log_WeighIn>;

  protected waistShown = false;
  protected calipersShown = false;
  // set once the user manually toggles a body-fat section, so the background
  // recent-weigh-in load does not overwrite their choice when it resolves.
  private bfSectionsUserAdjusted = false;
  // set when the recent-weigh-in load is waiting for the slide-in to finish (see determineCountdowns)
  private pendingCadenceLoad_local: DateTime | undefined;
  // counts the loads determineCountdowns has queued, so only the newest one gives the count back
  private queuedCadenceLoadId = 0;
  // true while this page holds one AnimationController count for that load (see holdCadenceTracking)
  private cadenceTrackingHeld = false;
  // set in ngOnDestroy. viewDestroyed cannot stand in: it marks a page that has LEFT the screen, and
  // it is still false while ionViewCanEnter runs, which is when a destroyed page can still resume
  private pageDestroyed = false;
  immutableState: ImmutableState = {} as ImmutableState;
  fitnessState: FitnessState;
  fitnessPlan: FitnessPlan;
  Gender = Gender;
  readonly = false; // for cs-skinfold-site-measure component
  isCoachReadOnly = false;

  dateStatus = DateStatus.Past;

  get isToday(): boolean {
    return this.dateStatus === DateStatus.Today;
  }

  get isFuture(): boolean {
    return this.dateStatus === DateStatus.Future;
  }

  get isDisabled(): boolean {
    return this.isFuture || this.isCoachReadOnly;
  }

  // Preselect values from last log entries
  lastWeightValue: number = undefined;
  lastSkinfoldValues: { [key: string]: number } = {};
  lastWaistValue: number = undefined;

  // Properties for cs-info-card display
  weightDaysAgo: number = 0;
  bfDaysAgo: number = 0;
  // Recommended body-comp cadence. Defaults to the new-user value; populated
  // from the prior log window in determineCountdowns() via
  // getRecommendedBfFrequencyDays — see shared/src/body-comp-cadence.ts.
  bfFrequencyDays: number = BF_FREQUENCY_NEW_USER_DAYS;
  hasPreviousWeightLog: boolean = false; // Track if user has any previous weight logs
  hasPreviousBfLog: boolean = false; // Track if user has any previous body fat logs
  showFirstTimeBodyFatInfo: boolean = false; // Show info message for first-time body fat users
  showBodyFatCooldownInfo: boolean = false; // Show info when in cooldown period
  showProgressCheckNeedsBodyFatInfo: boolean = false; // Show info when the progress check needs a body-fat reading (#6067)
  bfCooldownMessage: TranslatedString = untranslatedString(''); // Translated cooldown message

  get showWeightWarning(): boolean {
    return this.hasPreviousWeightLog && this.weightDaysAgo > 1; // Warn if more than 1 day since last weight
  }

  get showBfWarning(): boolean {
    // Warn if more than `bfFrequencyDays` days since last BF measurement
    // (3 for new users, 5 for grandfathered existing users).
    return this.hasPreviousBfLog && this.bfDaysAgo > this.bfFrequencyDays;
  }

  get showBodyFatFirstTimeInfo(): boolean {
    return this.showFirstTimeBodyFatInfo && !this.hasPreviousBfLog && this.dateStatus === DateStatus.Today;
  }

  get showProgressCheckNeedsBodyFat(): boolean {
    return this.showProgressCheckNeedsBodyFatInfo && this.dateStatus === DateStatus.Today;
  }

  get showBodyFatCooldown(): boolean {
    return this.showBodyFatCooldownInfo && this.dateStatus === DateStatus.Today;
  }

  get weightWarningMessage(): TranslatedString {
    if (this.weightDaysAgo > 30) {
      return LL.editWeighIn.warnings.weightNotMeasuredLongTime();
    } else if (this.weightDaysAgo >= 14) {
      return LL.editWeighIn.warnings.weightNotMeasuredWeeks({ weeks: Math.floor(this.weightDaysAgo / 7) });
    } else {
      return LL.editWeighIn.warnings.weightNotMeasured({ days: this.weightDaysAgo });
    }
  }

  get bfWarningMessage(): TranslatedString {
    if (this.bfDaysAgo > 30) {
      return LL.editWeighIn.warnings.bodyFatNotMeasuredLongTime({ frequency: this.bfFrequencyDays });
    } else if (this.bfDaysAgo >= 14) {
      return LL.editWeighIn.warnings.bodyFatNotMeasuredWeeks({ weeks: Math.floor(this.bfDaysAgo / 7), frequency: this.bfFrequencyDays });
    } else {
      return LL.editWeighIn.warnings.bodyFatNotMeasured({ days: this.bfDaysAgo, frequency: this.bfFrequencyDays });
    }
  }

  skinfoldsM = [
    {
      id: 'suprailiac_mm',
      name: skinfoldSiteName('suprailiac'),
      photo: 'assets/imgs/Skinfold-M-Suprailiac.svg',
    }, // (comment forces Prettier to break lines)
    {
      id: 'umbilical_mm',
      name: skinfoldSiteName('umbilical'),
      photo: 'assets/imgs/Skinfold-M-Umbilical.svg',
    },
  ];

  skinfoldsF = [
    {
      id: 'suprailiac_mm',
      name: skinfoldSiteName('suprailiac'),
      photo: 'assets/imgs/Skinfold-F-Suprailiac.svg',
    }, // (comment forces Prettier to break lines)
    {
      id: 'umbilical_mm',
      name: skinfoldSiteName('umbilical'),
      photo: 'assets/imgs/Skinfold-F-Umbilical.svg',
    },
    {
      id: 'thigh_mm',
      name: skinfoldSiteName('thigh'),
      photo: 'assets/imgs/Skinfold-F-Thigh.svg',
    },
  ];

  private unsavedChangesGuard: UnsavedChangesGuard<void>;
  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public auth: AuthService,
    private debug: DebugService,
    private telemetry: TelemetryService,
    
    private cdRef: ChangeDetectorRef,
    private pubSub: PubSubService,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private loadingCtrl: LoadingController,
    private notificationService: NotificationService,
    private logging: LoggingService,
    private animationController: AnimationController,
    private svPlanHandler: SvPlanHandlerService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    // just markForCheck — CouchDocDraft is a deep clone, so sync updates to the canonical
    // don't affect the user's active edit. The draft's save will merge via conflict handler.
    this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
      if (this.viewDestroyed) return;
      this.cdRef.markForCheck();
    });

    let id = this.navParams.data.id;
    if (id) {
      try {
        const loaded = await this.userDb.getOneLogEntryAsync<Log_WeighIn>(id, this.docWatcher);
        this.weighInDraft = new CouchDocDraft(loaded);
      } catch (err) {
        return this.auth.failPageGuardAsync(`Unknown weigh-in id: '${id}'`, { cause: err });
      }
    } else {
      const now = this.debug.getNowLocal();

      // getting here means somebody opened this page with no weigh-in to edit, which no
      // navigation is meant to do. Report it rather than absorbing it: it stays a PROBLEM
      // because nothing is repaired here, and it is the only way we learn whether the
      // create branch below is reachable at all.
      this.logging.trackSilentProblem('WeighInPageOpenedWithoutEntryId', {
        day: this.navParams.data.day,
      });

      // the new weigh-in belongs on the day the page was opened for, not on today. For today
      // this is exactly `now`.
      // #5013: pass the LOCAL time, never `.toUTC()` — the creator takes the entry's offset
      // from this argument. See design-docs/sync-integrity.md, "A Log Entry's Two Time Fields
      // Must Always Move Together".
      const entryTime = newEntryTimeForDay(this.navParams.data.day, now);
      const newEntry = this.userDb.createGenericEntry(entryTime, LogRecordType.WeighIn) as Log_WeighIn;
      this.weighInDraft = new CouchDocDraft(newEntry);
    }

    // create working copy for UI modifications
    this.weighIn = this.weighInDraft.draft;
    this.title = LL.editWeighIn.title();
    this.isCoachReadOnly = !!this.userDb.getCoachManagingClient();

    [this.immutableState, this.fitnessState, this.fitnessPlan] = await Promise.all([
      this.userDb.loadImmutableStateAsync(this.docWatcher),
      this.userDb.loadFitnessStateAsync(this.docWatcher),
      this.userDb.loadPlanAsync(this.docWatcher),
    ]);
    this.weighIn.tapeMeasures = this.weighIn.tapeMeasures || {};
    this.weighIn.tapeMeasures.waist_cm = this.weighIn.tapeMeasures.waist_cm || [];
    this.weighIn.caliperMeasures = this.weighIn.caliperMeasures || {};
    for (let s of this.getSkinfolds()) {
      this.weighIn.caliperMeasures[s.id] = this.weighIn.caliperMeasures[s.id] || [];
    }

    // append date to meals that are not today
    let now_local = this.debug.getNowLocal();
    let logDate_local = getEntryLocalTimeInDesiredTimeZone(this.weighIn, now_local);

    // compare ISO dates for accurate date comparison
    const todayISO = now_local.toISODate();
    const logDateISO = logDate_local.toISODate();

    if (logDateISO === todayISO) {
      this.dateStatus = DateStatus.Today;
    } else if (logDateISO > todayISO) {
      this.dateStatus = DateStatus.Future;
    } else {
      this.dateStatus = DateStatus.Past;
    }

    if (this.dateStatus !== DateStatus.Today) {
      this.title += ', ' + formatRelativeDay(now_local, logDate_local);
    }

    // Decide body-fat section visibility now (cheap, synchronous). The "today,
    // nothing logged yet" case needs the recent-weigh-in load, which waits for
    // the slide-in to finish (ionViewDidEnter); the cadence + info cards appear
    // when it resolves. See determineCountdowns / loadRecentWeighInCadenceAsync.
    this.determineCountdowns(now_local);

    this.safeMarkForCheck(this.cdRef);

  }

  // does this weigh-in have a real weight value?
  private logContainsWeight(w: Log_WeighIn): boolean {
    return !!(w.weight_kg && w.weight_kg > 0);
  }

  // Decide which body-fat sections to show. The past/future and
  // already-has-body-fat cases are cheap and resolved synchronously here. The
  // "today, nothing logged yet" case needs the recent-weigh-in history to pick
  // the cadence and last-measurement warnings — that load runs after the
  // slide-in (loadRecentWeighInCadenceAsync, started from ionViewDidEnter).
  private determineCountdowns(now_local: DateTime): void {
    // if this is not today
    if (this.dateStatus !== DateStatus.Today) {
      // if this is in the past
      if (this.dateStatus === DateStatus.Past) {
        // allow the user to edit the existing data
        this.calipersShown = hasAnyCaliperReadings(this.weighIn);
        this.waistShown = hasAnyTapeReadings(this.weighIn);
        // if no data, show both options
        if (!this.calipersShown && !this.waistShown) {
          this.calipersShown = true;
          this.waistShown = true;
        }
      } else if (this.dateStatus === DateStatus.Future) {
        // a future date is read-only (isDisabled), so leave both body-fat
        // sections hidden — there is nothing to prompt or edit yet.
      }
      return;
    }

    // if this entry already has calipers or tape measures, show them
    if (hasMeasuredBodyFat(this.weighIn)) {
      this.calipersShown = hasAnyCaliperReadings(this.weighIn);
      this.waistShown = hasAnyTapeReadings(this.weighIn);
      return;
    }

    // today with nothing logged yet: compute the cadence + warnings from recent
    // history, but only once the slide-in has finished. That load and the
    // progress check run on the main thread, and running them during the slide
    // made it stutter on the first open after an app start, when nothing was
    // cached yet. ionViewDidEnter starts it.
    this.pendingCadenceLoad_local = now_local;
    this.queuedCadenceLoadId++;
    this.holdCadenceTracking();
  }

  // Count the pending cadence load as a running animation from now until it
  // finishes, so E2E's waitPageLoad blocks until the cadence-driven state (info
  // cards, body-fat section toggles) has settled. It is taken here, before the
  // slide, rather than in ionViewDidEnter, so there is no moment between the
  // slide ending and the load starting where the page looks settled. It does
  // not block the user, only the test's "is the page settled" check.
  private holdCadenceTracking(): void {
    // if the page was destroyed while ionViewCanEnter was still awaiting, nothing would give
    // the count back: ngOnDestroy has run and ionViewDidEnter never will
    if (this.pageDestroyed || this.cadenceTrackingHeld) return;
    this.cadenceTrackingHeld = true;
    this.animationController.onAnimationStarted();
  }

  // Give back the count taken by holdCadenceTracking. Called when the load
  // finishes, and from ngOnDestroy for a page closed before its slide ended
  // (cs-nav skips ionViewDidEnter for it, so the load never starts).
  private releaseCadenceTracking(): void {
    if (!this.cadenceTrackingHeld) return;
    this.cadenceTrackingHeld = false;
    this.animationController.onAnimationEnded();
  }

  // Ask the progress check whether it needs a body-fat reading before it can judge progress.
  // It only adds to the measuring rhythm, so any failure answers false and the page falls back
  // to the rhythm rather than losing its whole history-based setup. A failed solve was already
  // reported by the service; anything else (e.g. the database could not be read) is reported here.
  private async progressCheckNeedsBodyFatAsync(): Promise<boolean> {
    try {
      const checkResult = await this.svPlanHandler.checkForPlanAdjustments();
      return checkResult.needsMoreBodyFatReadings;
    } catch (error) {
      if (!isPlanCheckFailed(error)) {
        this.logging.trackSilentProblem('EditWeighIn: progress check for body-fat cadence failed', { error: describeError(error) });
      }
      return false;
    }
  }

  // Load the user's recent weigh-ins (60-day window) and seed the cadence +
  // last-measurement warnings from them. Runs in the background after the page
  // has opened, then marks for check so the info cards and body-fat prompts
  // appear when ready. Phase 3: when there are ≥6 Done caliper or tape entries
  // in the last 60 days, the helper computes per-user noise (rms/iqr) and picks
  // 5 / 3 / 2 days by tercile (caliper preferred); otherwise it falls back to
  // the signal-based new-vs-existing logic (3 vs 5 days). The 60-day window
  // matches NOISE_WINDOW_DAYS in the shared helper.
  private async loadRecentWeighInCadenceAsync(now_local: DateTime): Promise<void> {
    try {
      const todayBoD_local = now_local.startOf('day');
      // the progress check runs alongside the history read; it says whether the plan is
      // waiting on a body-fat reading the rhythm alone would not ask for yet (#6067)
      // the history comes straight from the database: only saved weigh-ins matter
      // here, and reading through the backlog cache rebuilt up to 75 uncached days
      // on the first open after an app start (see getRecentStoredLogEntriesAsync)
      const [loadedWeighIns, progressCheckNeedsBodyFat] = await Promise.all([
        this.userDb.getRecentStoredLogEntriesAsync<Log_WeighIn>(LogRecordType.WeighIn, todayBoD_local, 60),
        this.progressCheckNeedsBodyFatAsync(),
      ]);

      // if the user navigated away while the read was in flight, stop
      if (this.viewDestroyed) return;

      // the database read only has saved entries, and the entry this page edits may not be saved
      // yet. The cached read this replaced always held it (it is a day's planned weigh-in), and
      // the rules below look at the newest entries, so put the same unchanged entry back in
      // rather than let the answer depend on whether Home's save of it had landed
      const editedEntry = this.weighInDraft.canonical;
      if (!loadedWeighIns.some(w => w._id === editedEntry._id)) {
        loadedWeighIns.push(editedEntry);
        loadedWeighIns.sort((a, b) => (a.time_utc ?? '').localeCompare(b.time_utc ?? ''));
      }

      const recentWeighIns = loadedWeighIns.reverse();
      this.bfFrequencyDays = getRecommendedBfFrequencyDays(recentWeighIns, now_local, this.immutableState.gender);

      // find how long ago the user entered weight and body-fat measurements
      const latestWeightLog = recentWeighIns.find(w => this.logContainsWeight(w));
      const latestBFLog = recentWeighIns.find(w => hasMeasuredBodyFat(w));
      const lastBfLogHasTape = hasAnyTapeReadings(latestBFLog);
      const lastBfLogHasCalipers = hasAnyCaliperReadings(latestBFLog);

      // store last weight value for preselection
      if (latestWeightLog && latestWeightLog.weight_kg > 0) {
        this.lastWeightValue = latestWeightLog.weight_kg;
      }

      // store last waist value for preselection — opens the waist roller on
      // the user's last recorded value instead of the fixed 35 cm default
      // (#3894), mirroring the weight preselect above and the skinfold
      // preselect below. Stays undefined when there is no prior waist reading,
      // so the input falls back to its own default.
      this.lastWaistValue = getLastMeasuredWaist_cm(recentWeighIns);

      // store last skinfold values for preselection
      if (latestBFLog && latestBFLog.caliperMeasures) {
        for (let skinfold of this.getSkinfolds()) {
          if (latestBFLog.caliperMeasures[skinfold.id] && latestBFLog.caliperMeasures[skinfold.id].length > 0) {
            // find the first non-null value from the measurements
            const validMeasurement = latestBFLog.caliperMeasures[skinfold.id].find((m: number) => m > 0);
            if (validMeasurement) {
              this.lastSkinfoldValues[skinfold.id] = validMeasurement;
            }
          }
        }
      }

      const daysElapsedSinceLog = (logEntry: Log_WeighIn) => Math.round(todayBoD_local.diff(getEntryLocalTimeInDesiredTimeZone(logEntry, todayBoD_local), 'days').as('days'));

      const weightDaysAgo = latestWeightLog ? daysElapsedSinceLog(latestWeightLog) : 99;
      const bfDaysAgo = latestBFLog ? daysElapsedSinceLog(latestBFLog) : 99;

      // check if user has previous logs for each type
      this.hasPreviousWeightLog = latestWeightLog !== undefined;
      this.hasPreviousBfLog = latestBFLog !== undefined;

      // assign to component properties for use in template
      this.weightDaysAgo = weightDaysAgo;
      this.bfDaysAgo = bfDaysAgo;

      // compute the body-fat section visibility this history implies
      const sections = decideBodyFatSections({
        hasPreviousBfLog: latestBFLog !== undefined,
        bfDaysAgo,
        bfFrequencyDays: this.bfFrequencyDays,
        lastBfLogHasCalipers,
        lastBfLogHasTape,
        progressCheckNeedsBodyFat,
      });
      this.showProgressCheckNeedsBodyFatInfo = sections.showProgressCheckNeedsBodyFat;
      if (sections.showFirstTimeInfo) {
        this.showFirstTimeBodyFatInfo = true;
      }
      if (sections.showCooldown) {
        this.showBodyFatCooldownInfo = true;
        if (sections.cooldownDaysLeft === 1) {
          this.bfCooldownMessage = LL.editWeighIn.info.bodyFatCooldownTomorrow();
        } else {
          const againAtDate_local = todayBoD_local.plus({ days: sections.cooldownDaysLeft });
          this.bfCooldownMessage = LL.editWeighIn.info.bodyFatCooldownDays({
            days: sections.cooldownDaysLeft,
            date: formatRelativeDay(todayBoD_local, againAtDate_local)
          });
        }
      }

      // apply the section visibility unless the user already toggled the
      // sections themselves while this was loading - don't overwrite their choice
      if (!this.bfSectionsUserAdjusted) {
        this.calipersShown = sections.calipersShown;
        this.waistShown = sections.waistShown;
      }

      this.safeMarkForCheck(this.cdRef);
    } catch (err) {
      // the page already opened; surface the failure so the missing cadence /
      // warnings are visible in telemetry rather than silently swallowed.
      this.logging.trackAbnormalException(err);
    }
  }

  // the user manually toggled a body-fat section. Remember it so the background
  // cadence load does not overwrite their choice when it resolves.
  protected onBfSectionToggle(section: 'calipers' | 'waist', shown: boolean): void {
    if (section === 'calipers') {
      this.calipersShown = shown;
    } else {
      this.waistShown = shown;
    }
    this.bfSectionsUserAdjusted = true;
  }

  getSkinfolds() {
    return this.immutableState.gender === Gender.male ? this.skinfoldsM : this.skinfoldsF;
  }

  ngAfterViewInit() {
    // snapshot for change detection - taken after view initialization
    this.weighInInitial = deepClone(this.weighIn);

    // create unsaved changes guard for ionViewCanLeave
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard(
      [this.weighIn],
      async () => { await this.saveAsync(); },
      {}
    );
  }

  async ionViewDidEnter() {
    // Navigation history is now handled automatically by trail-based navigation

    // if determineCountdowns left the recent-weigh-in load for after the slide, run it now, once
    // (this hook runs again on every return from a page opened on top of this one)
    const pendingNow_local = this.pendingCadenceLoad_local;
    if (!pendingNow_local) return;

    this.pendingCadenceLoad_local = undefined;
    const loadId = this.queuedCadenceLoadId;
    try {
      await this.loadRecentWeighInCadenceAsync(pendingNow_local);
    } finally {
      // if a page opened on top and closed again while this load ran, ionViewCanEnter queued
      // another load, and the count now belongs to that one
      if (loadId === this.queuedCadenceLoadId) {
        this.releaseCadenceTracking();
      }
    }
  }

  async ionViewWillLeave() {
    // working copy is simply abandoned - no restoration needed with the new pattern

    const hasWeight = this.weighIn.weight_kg > 0;
    // these two ask whether something was really measured, so a zero-only row
    // counts as "not measured" — unlike the section-visibility flags above,
    // which are satisfied by any row of slots whatever those slots hold
    const hasCalipers = hasMeasuredSkinfolds(this.weighIn);
    const hasTapes = hasMeasuredWaist(this.weighIn);
    this.telemetry.trackEvent('record_weighin', { weight: hasWeight, calipers: hasCalipers, tapes: hasTapes });
  }


  // used by [validity] property in vld-error component in the form
  validity(field: string): string {
    switch (field) {
      case 'weight':
        {
          if (this.weighIn.weight_kg == null) {
            return LL.editWeighIn.validation.weight();
          }
        }
        break;
      case 'waist':
        {
          // let count = this.weighIn.tapeMeasures.waist_cm.filter(x => x !== undefined).length;
          // if (count > 0 && count < 3) {
          //   // allow no measures, but not incomplete measures
          //   return 'Measure your waist three times and we will calculate the average.';
          // }
          return null;
        }
      case 'calipers':
        {
          // Count readings only at the sites this user is asked to measure,
          // because the "complete" threshold below comes from that same list —
          // two sites for a male user, three for a female one. Counting a site
          // the user is never shown (old thigh readings on a male account, say)
          // used to be compared against a threshold that left that site out.
          let count = 0;
          const caliperMeasures = this.weighIn.caliperMeasures;
          // for each skinfold site this user measures
          for (let skinfold of this.getSkinfolds()) {
            const readings = caliperMeasures && caliperMeasures[skinfold.id];
            if (readings) {
              count += readings.filter(x => x !== undefined).length;
            }
          }
          if (count > 0 && count < this.getSkinfolds().length * 5) {
            // allow no measures, but not incomplete measures
            return LL.editWeighIn.validation.skinfolds();
          }
        }
        break;
      default:
        return LL.common.errors.unknownField();
    }
    return null;
  }

  async saveAsync() {
    if (this.isCoachReadOnly) return;
    let transaction = DatabaseWrapper.getTransactionId();
    // Set LogRecordState to Done when user enters weight, weigh-in is then complete.
    this.weighIn.state = this.weighIn.weight_kg ? LogRecordState.Done : this.weighIn.state;

    const canonical = this.weighInDraft.commitDraft();
    await this.userDb.saveEntryAsync(transaction, canonical);

    // user saved a weight — suppress today's weigh-in reminder (fire-and-forget)
    if (canonical.state === LogRecordState.Done) {
      void this.notificationService.suppressForEntryAsync(canonical, this.debug.getNowLocal());
    }

    const today = this.debug.getNowLocal();
    if (this.dateStatus === DateStatus.Today) {
      // Only sync when the weigh-in entry actually carries a weight. A
      // tape-only or caliper-only entry — or any save without a weight value —
      // must not wipe fitnessState.weight_kg, otherwise plan recalculation
      // throws "Unable to find any bodyweight." (Sentry CYBERNETIC-FITNESS-APP-369, #3286).
      if (this.weighIn.weight_kg && this.fitnessState.weight_kg !== this.weighIn.weight_kg) {
        this.fitnessState.weight_kg = this.weighIn.weight_kg;
        await this.userDb.saveFitnessStateAsync(transaction);
      }

      if (!this.fitnessPlan.shouldCheckWhetherNeedsReadjustment) {
        if (!this.fitnessPlan.lastTimeMissedNutritionMacros_utc ||
          today.diff(DateTime.fromISO(this.fitnessPlan.lastTimeMissedNutritionMacros_utc).toLocal(), 'days').as("days") > 7) {
          this.fitnessPlan.shouldCheckWhetherNeedsReadjustment = true;
          this.fitnessPlan.lastTimeMissedNutritionMacros_utc = undefined;
        }
        await this.userDb.savePlanAsync(transaction);
      }
    }

    // clear the cache to ensure fresh data is loaded when returning to home page
    //await this.userDb.clearBacklogCacheAsync();

    // publish events so that charts and other UI components update
    this.pubSub.publish(DayUpdatedEvent, {});
    this.pubSub.publish(BodyCompositionDataChangedEvent, {});

    // update initial snapshot after save to reflect new baseline
    this.weighInInitial = deepClone(this.weighIn);
  }

  somethingChanged() {
    // error messages are only checked once at least something is entered
    this.checkValues = true;
    this.vldGroup.onNextClicked(null);
    this.isModified = !deepCompareEQ(this.weighIn, this.weighInInitial);
    this.cdRef.markForCheck();
  }

  public isModified = false;
  handleSaveButton(event: Event) {
    lockClick(event, this.handleSaveButtonAsync());
  }

  private async handleSaveButtonAsync() {
    // show loading overlay after 500ms — fast saves won't flicker
    const loading = this.loadingCtrl.create({ content: LL.common.buttons.saving() });
    const showTimer = this.safeTimeout(() => { void loading.present(); }, 500);

    try {
      await this.unsavedChangesGuard.saveAndExitAsync(() => !this.viewDestroyed);
    } finally {
      this.cancelTimeout(showTimer);
      void loading.dismiss();
    }
  }

  /**
   * Called by navigation system for ALL back navigation (header button, browser back, swipe).
   * Shows save/discard dialog if there are unsaved changes.
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<void>> {
    return this.unsavedChangesGuard.confirmLeaveAsync();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.pageDestroyed = true;
    this.releaseCadenceTracking();
  }
}
